'use client';

// Two-dropdown product selector (FR-8/FR-9/FR-10, Pitch #1). Open.cx is pinned
// to the top with a divider + "Recommended" tag, and is NOT auto-selected. The
// reader picks two products, then clicks Compare to navigate to the canonical
// URL (a real page load) — no auto-jump on selection.

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { canonicalPairSlug, comparePath } from '@/lib/compare/slug';
import { track } from '@/lib/compare/track';

export interface ProductOption {
  name: string;
  urlSlug: string;
  isOpen: boolean;
}

function Dropdown({
  options,
  value,
  exclude,
  placeholder,
  onChange,
}: {
  options: ProductOption[];
  value?: string;
  exclude?: string;
  placeholder: string;
  onChange: (slug: string) => void;
}) {
  const open = options.find((o) => o.isOpen);
  const rest = options.filter((o) => !o.isOpen);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11 bg-white text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        <SelectGroup>
          {open && (
            <SelectItem value={open.urlSlug} disabled={open.urlSlug === exclude}>
              <span className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-neutral-900" />
                <span className="font-medium text-neutral-900">{open.name}</span>
                <span className="ml-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                  Recommended
                </span>
              </span>
            </SelectItem>
          )}
          {rest.map((o) => (
            <SelectItem key={o.urlSlug} value={o.urlSlug} disabled={o.urlSlug === exclude}>
              {o.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export function ProductSelector({
  vertical,
  options,
  valueA,
  valueB,
}: {
  vertical: string;
  options: ProductOption[];
  valueA?: string;
  valueB?: string;
}) {
  const router = useRouter();
  const [a, setA] = useState<string | undefined>(valueA);
  const [b, setB] = useState<string | undefined>(valueB);

  // Re-seed from props when the viewed pair changes (e.g. client navigation to
  // a related comparison). useState only runs on mount, so without this the
  // dropdowns would keep showing the previous pair. Render-phase sync rather
  // than an effect, per React's "resetting state when a prop changes" pattern.
  const [seededA, setSeededA] = useState(valueA);
  const [seededB, setSeededB] = useState(valueB);
  if (valueA !== seededA || valueB !== seededB) {
    setSeededA(valueA);
    setSeededB(valueB);
    setA(valueA);
    setB(valueB);
  }

  const ready = Boolean(a && b && a !== b);

  const submit = () => {
    if (!ready) return;
    const slug = canonicalPairSlug(a!, b!);
    track('compare_selector_submit', { vertical, a, b, slug });
    router.push(comparePath(vertical, slug));
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <Dropdown
            options={options}
            value={a}
            exclude={b}
            placeholder="Choose a product"
            onChange={setA}
          />
        </div>
        <span className="shrink-0 text-center text-sm font-medium text-neutral-400">vs</span>
        <div className="min-w-0 flex-1">
          <Dropdown
            options={options}
            value={b}
            exclude={a}
            placeholder="Choose a product"
            onChange={setB}
          />
        </div>
      </div>
      <Button
        type="button"
        onClick={submit}
        disabled={!ready}
        className="h-11 shrink-0 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
      >
        Compare
        <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    </div>
  );
}
